import { InboxOutlined } from '@ant-design/icons';
import type { ExpenseBankImportDecision, ExpenseBankImportPreview, ExpenseBankTxPreviewRow } from '@financiar/shared';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Checkbox, Modal, Select, Space, Spin, Table, Tag, Tooltip, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, formatDate, MoneyText } from '../../lib/format';
import { commitExpenseBankImport, previewExpenseBankImport } from './api';

const { Dragger } = Upload;
const { Text } = Typography;

type ImportPhase = 'upload' | 'preview' | 'importing';

/** Sentinel Select values — distinct from an expense id. */
const IGNORE_VALUE = '__ignore__';
const CREATE_VALUE = '__create__';

function summaryMessage(preview: ExpenseBankImportPreview): string {
  const parts: string[] = [];
  if (preview.summary.auto > 0) parts.push(`${preview.summary.auto} automat${preview.summary.auto === 1 ? 'ă' : 'e'}`);
  if (preview.summary.suggestions > 0) parts.push(`${preview.summary.suggestions} sugesti${preview.summary.suggestions === 1 ? 'e' : 'i'}`);
  if (preview.summary.unmatched > 0) parts.push(`${preview.summary.unmatched} nepotrivit${preview.summary.unmatched === 1 ? 'ă' : 'e'}`);
  if (preview.summary.alreadyImported > 0) parts.push(`${preview.summary.alreadyImported} deja importat${preview.summary.alreadyImported === 1 ? 'ă' : 'e'}`);

  const newAccounts = preview.accounts.filter((account) => account.willCreate);
  const base = `${preview.summary.debits} plăț${preview.summary.debits === 1 ? 'i' : 'i'} găsite${parts.length > 0 ? ` (${parts.join(', ')})` : ''}. Transferurile între conturile proprii au fost excluse.`;
  return newAccounts.length > 0
    ? `${base} Se creează cont${newAccounts.length === 1 ? '' : 'uri'} nou${newAccounts.length === 1 ? '' : 'i'}: ${newAccounts.map((account) => account.iban).join(', ')}.`
    : base;
}

/** SUGGESTION rows pre-fill their proposed expense; recognizable documentless
 * outflows (fees/taxes/salaries) pre-fill their quick-create. Everything else
 * starts undecided and is simply stored for the reconciliation workspace. */
function initialDecisions(preview: ExpenseBankImportPreview): Record<string, ExpenseBankImportDecision> {
  const decisions: Record<string, ExpenseBankImportDecision> = {};
  for (const row of preview.rows) {
    if (row.alreadyImported) continue;
    if (row.match.kind === 'suggestion' && row.match.expenseId) {
      decisions[row.dedupHash] = { action: 'match', expenseId: row.match.expenseId };
    } else if (row.match.kind === 'unmatched' && row.match.quickCreate) {
      decisions[row.dedupHash] = {
        action: 'create',
        kind: row.match.quickCreate.kind,
        categoryName: row.match.quickCreate.categoryName,
        description: row.match.quickCreate.description,
      };
    }
  }
  return decisions;
}

/** Rows this commit would allocate or create an expense for. */
function countToRecord(preview: ExpenseBankImportPreview, decisions: Record<string, ExpenseBankImportDecision>): number {
  return preview.rows.filter((row) => {
    if (row.alreadyImported) return false;
    const decision = decisions[row.dedupHash];
    if (row.match.kind === 'auto') return decision?.action !== 'ignore';
    return decision?.action === 'match' || decision?.action === 'create';
  }).length;
}

function MatchCell({
  row,
  decision,
  onChange,
}: {
  row: ExpenseBankTxPreviewRow;
  decision: ExpenseBankImportDecision | undefined;
  onChange: (decision: ExpenseBankImportDecision | undefined) => void;
}) {
  if (row.alreadyImported) {
    return <Tag color="default">Deja importată</Tag>;
  }

  if (row.match.kind === 'auto') {
    const checked = decision?.action !== 'ignore';
    return (
      <Space direction="vertical" size={4}>
        <Tag color="green">{row.match.docLabel} ✓</Tag>
        <Checkbox checked={checked} onChange={(event) => onChange(event.target.checked ? undefined : { action: 'ignore' })}>
          alocă plata
        </Checkbox>
      </Space>
    );
  }

  const quickCreate = row.match.kind === 'unmatched' ? row.match.quickCreate : undefined;
  const alternatives = [...(row.match.alternatives ?? [])].sort((a, b) => a.docDate.localeCompare(b.docDate));
  const selectValue =
    decision?.action === 'ignore'
      ? IGNORE_VALUE
      : decision?.action === 'create'
        ? CREATE_VALUE
        : decision?.action === 'match'
          ? decision.expenseId
          : undefined;
  const options = [
    ...alternatives.map((alt) => ({
      value: alt.id,
      label: `${alt.docLabel} — ${alt.docDate ? formatDate(alt.docDate) : '—'}${alt.remaining ? ` — rest ${alt.remaining}` : ''}`,
    })),
    {
      value: CREATE_VALUE,
      label: quickCreate ? `Creează cheltuială: ${quickCreate.categoryName}` : 'Creează cheltuială (Altele)',
    },
    { value: IGNORE_VALUE, label: 'Ignoră definitiv' },
  ];

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {row.match.kind === 'suggestion' ? (
        <Tooltip title={row.match.reason}>
          <Tag color="gold">Sugestie</Tag>
        </Tooltip>
      ) : (
        <Tooltip title={row.match.reason}>
          <Tag color="default">Nepotrivită</Tag>
        </Tooltip>
      )}
      <Select
        style={{ width: '100%', minWidth: 260 }}
        size="small"
        allowClear
        placeholder="Lasă nereconciliată"
        value={selectValue}
        options={options}
        onChange={(value: string | undefined) => {
          if (value === undefined) onChange(undefined);
          else if (value === IGNORE_VALUE) onChange({ action: 'ignore' });
          else if (value === CREATE_VALUE)
            onChange(
              quickCreate
                ? { action: 'create', kind: quickCreate.kind, categoryName: quickCreate.categoryName, description: quickCreate.description }
                : { action: 'create', kind: 'OTHER', description: row.description },
            );
          else onChange({ action: 'match', expenseId: value });
        }}
      />
    </Space>
  );
}

/**
 * "Importă extras (plăți)" — the debit-side twin of `BankImportModal`
 * (features/facturi): same upload → interactive preview → commit shape, but
 * rows are matched against open EXPENSES, recognizable fees/taxes offer a
 * pre-filled "Creează cheltuială", and undecided rows are still stored for
 * the reconciliation workspace instead of being dropped.
 */
export function ExpenseBankImportModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<ExpenseBankImportPreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ExpenseBankImportDecision>>({});
  const [showImported, setShowImported] = useState(false);

  function resetToUpload() {
    setPhase('upload');
    setFileList([]);
    setFiles([]);
    setPreview(null);
    setDecisions({});
    setShowImported(false);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  const previewMutation = useMutation({
    mutationFn: (uploaded: File[]) => previewExpenseBankImport(uploaded),
    onSuccess: (data, uploaded) => {
      setPreview(data);
      setFiles(uploaded);
      setDecisions(initialDecisions(data));
      setPhase('preview');
    },
    onError: (error) => {
      message.error(getApiError(error)?.message ?? 'Extrasele nu au putut fi analizate.');
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => commitExpenseBankImport(files, decisions),
    onMutate: () => setPhase('importing'),
    onSuccess: (result) => {
      const parts = [`${result.transactionsStored} tranzacți${result.transactionsStored === 1 ? 'e importată' : 'i importate'}`];
      if (result.paymentsRecorded > 0) parts.push(`${result.paymentsRecorded} plăț${result.paymentsRecorded === 1 ? 'i' : 'i'} alocate`);
      if (result.expensesCreated > 0) parts.push(`${result.expensesCreated} cheltuiel${result.expensesCreated === 1 ? 'ă' : 'i'} noi`);
      if (result.accountsCreated > 0) parts.push(`${result.accountsCreated} cont${result.accountsCreated === 1 ? '' : 'uri'} nou${result.accountsCreated === 1 ? '' : 'i'}`);
      if (result.skippedDuplicates > 0) parts.push(`${result.skippedDuplicates} deja importate`);
      if (result.failed > 0) parts.push(`${result.failed} eșuate`);
      message.success(`${parts.join(', ')}.`);
      onDone();
      handleClose();
    },
    onError: (error) => {
      setPhase('preview');
      message.error(getApiError(error)?.message ?? 'Importul a eșuat.');
    },
  });

  const columns: ColumnsType<ExpenseBankTxPreviewRow> = [
    { title: 'Data', key: 'transactionDate', width: 100, render: (_, row) => <DateText value={row.transactionDate} /> },
    { title: 'Sumă', key: 'amount', width: 110, align: 'right', render: (_, row) => <MoneyText value={row.amount} /> },
    {
      title: 'Beneficiar',
      key: 'counterparty',
      width: 200,
      ellipsis: true,
      render: (_, row) => (row.counterpartyName ? `${row.counterpartyName}${row.counterpartyCif ? ` (${row.counterpartyCif})` : ''}` : '—'),
    },
    {
      title: 'Descrierea tranzacției',
      dataIndex: 'description',
      ellipsis: true,
      render: (value: string | null) => <Tooltip title={value}>{value || '—'}</Tooltip>,
    },
    { title: 'Cont', dataIndex: 'accountIban', width: 130, ellipsis: true, render: (value: string) => <Tooltip title={value}>{value}</Tooltip> },
    {
      title: 'Potrivire',
      key: 'match',
      width: 300,
      render: (_, row) => (
        <MatchCell
          row={row}
          decision={decisions[row.dedupHash]}
          onChange={(decision) =>
            setDecisions((prev) => {
              const next = { ...prev };
              if (decision) next[row.dedupHash] = decision;
              else delete next[row.dedupHash];
              return next;
            })
          }
        />
      ),
    },
  ];

  const recordCount = preview ? countToRecord(preview, decisions) : 0;

  return (
    <Modal
      title="Importă extras bancar (plăți)"
      open={open}
      onCancel={handleClose}
      width="80vw"
      style={{ top: '8vh', paddingBottom: 0 }}
      styles={{ body: { maxHeight: '76vh', overflowY: 'auto' } }}
      destroyOnHidden
      footer={
        phase === 'upload'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Anulează
              </Button>,
              <Button
                key="analyze"
                type="primary"
                disabled={fileList.length === 0}
                loading={previewMutation.isPending}
                onClick={() => previewMutation.mutate(fileList.map((file) => file.originFileObj as File).filter(Boolean))}
              >
                Analizează {fileList.length > 0 ? fileList.length : ''} fișier{fileList.length === 1 ? '' : 'e'}
              </Button>,
            ]
          : [
              <Button key="back" disabled={phase === 'importing'} onClick={resetToUpload}>
                Înapoi
              </Button>,
              <Button key="cancel" disabled={phase === 'importing'} onClick={handleClose}>
                Anulează
              </Button>,
              <Button key="import" type="primary" loading={commitMutation.isPending} onClick={() => commitMutation.mutate()}>
                Importă ({recordCount} alocăr{recordCount === 1 ? 'e' : 'i'}/creări)
              </Button>,
            ]
      }
    >
      {phase === 'upload' ? (
        <Spin spinning={previewMutation.isPending} tip="Se analizează extrasele…">
          <Dragger
            accept=".xlsx,.zip"
            multiple
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: nextList }) => setFileList(nextList)}
            onRemove={(file) => {
              setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
              return true;
            }}
            style={{ padding: 24 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Trage extrasele aici sau apasă pentru a le selecta</p>
            <p className="ant-upload-hint">
              Extras Raiffeisen (.xlsx sau .zip). Se procesează doar plățile (debitele); transferurile între conturile proprii se
              exclud automat.
            </p>
          </Dragger>
        </Spin>
      ) : (
        <Spin spinning={phase === 'importing'} tip="Se importă…">
          {preview && (
            <>
              <Alert
                style={{ marginBottom: 12 }}
                type={preview.summary.unmatched > 0 ? 'warning' : 'info'}
                showIcon
                message={summaryMessage(preview)}
              />
              {preview.summary.alreadyImported > 0 && (
                <Checkbox checked={showImported} onChange={(event) => setShowImported(event.target.checked)} style={{ marginBottom: 12 }}>
                  Arată și cele {preview.summary.alreadyImported} deja importate
                </Checkbox>
              )}
              <Table<ExpenseBankTxPreviewRow>
                rowKey="dedupHash"
                size="small"
                columns={columns}
                dataSource={showImported ? preview.rows : preview.rows.filter((row) => !row.alreadyImported)}
                pagination={false}
                scroll={{ y: 'calc(76vh - 210px)' }}
                rowClassName={(row) => (row.alreadyImported ? 'bank-import-row-disabled' : '')}
                locale={{ emptyText: 'Nicio plată de importat — totul e deja importat.' }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Rândurile fără decizie se importă ca nereconciliate — le găsești ulterior în „Tranzacții nereconciliate".
              </Text>
            </>
          )}
        </Spin>
      )}
    </Modal>
  );
}
